import React, { Component } from 'react';

// 横向滚动条的唯一出口（知识库分类条 / 富消息 carousel 共用）。
//
// 为什么要有这个组件：`overflow-x: auto` + 隐藏滚动条 = 桌面端的死角 ——
//   1) 看不出右边还有内容（滚动条藏了，胶囊又是切齐的，边界毫无暗示）；
//   2) 鼠标按住拖不动（原生只认滚轮横扫/触控板双指），而拖动恰恰是桌面直觉；
//   3) 纵向滚轮停在这一行上时什么都不会发生。
// 三件事一起补：两端渐隐遮罩 + 指针拖拽 + 滚轮转横滚。触摸设备一概不插手，
// 原生惯性滑动比任何 JS 都好。
//
// 拖拽与点击的分界：位移超过 DRAG_THRESHOLD 才算拖，未超过的 pointerup 照常
// 冒泡成 click（分类照选不误）；超过了就在捕获阶段吞掉这一次 click，避免
// “拖到别的胶囊上松手 → 顺手把分类切了”。
//
// 两端遮罩的显隐只看 atStart/atEnd：不溢出时两者同时为真，两片遮罩自然都不显形。

const DRAG_THRESHOLD = 5;  // px，超过即判定为拖拽
const FADE_W = 24;         // px，两端遮罩宽度

const SCROLLER = {
    overflowX: 'auto',
    overflowY: 'hidden',
    // 滚动条不显形（触屏直接滑，桌面按住拖 / 滚轮）
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    WebkitOverflowScrolling: 'touch',
    overscrollBehaviorX: 'contain',
    // Tab 落到最边上的项时留出遮罩宽度，焦点环不被渐隐盖住
    scrollPaddingInline: `${FADE_W}px`,
};

const MASK = {
    position: 'absolute',
    top: 0,
    bottom: 1,               // 让开容器自己的 1px 下边框
    width: `${FADE_W}px`,
    pointerEvents: 'none',   // 遮罩不吃点击
};

export function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

// 把某个子元素滚进视野。reduced-motion 下直接跳，不做平滑动画。
export function scrollItemIntoView(el, inline) {
    if (!el || !el.scrollIntoView) return;
    el.scrollIntoView({
        block: 'nearest',
        inline: inline || 'nearest',
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
}

export default class HScrollRow extends Component {
    constructor(props) {
        super(props);
        // 初始都当作“没溢出”：遮罩默认不显形，量过尺寸再说
        this.state = { atStart: true, atEnd: true, hover: false };
        // 箭头只给「真鼠标」：触摸设备滑动是直觉，箭头纯占地方
        this._fine = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
        this._el = null;
        this._raf = 0;
        this._drag = null;
        this._eat = false;
        this._page = 0;   // 最近一次上报给 onPageChange 的页码
    }

    componentDidUpdate() {
        this._sched();   // children 变了（分类加载完 / 卡片增删）→ 重新量溢出
    }

    componentWillUnmount() {
        this._detach();
        if (this._raf) cancelAnimationFrame(this._raf);
    }

    setRef = (el) => {
        if (this._el === el) return;
        this._detach();
        this._el = el;
        if (el) {
            el.addEventListener('scroll', this._sched, { passive: true });
            // wheel 必须非被动：React 合成 onWheel 在 root 上是 passive 的，
            // preventDefault 会被忽略，横滚就抢不过外层纵向滚动。
            el.addEventListener('wheel', this._onWheel, { passive: false });
            el.addEventListener('pointerdown', this._onDown);
            el.addEventListener('click', this._onClick, true);
            if (window.ResizeObserver) {
                this._ro = new ResizeObserver(this._sched);
                this._ro.observe(el);
            }
            this._sched();
        }
    }

    _detach() {
        if (this._ro) { this._ro.disconnect(); this._ro = null; }
        const el = this._el;
        if (!el) return;
        el.removeEventListener('scroll', this._sched);
        el.removeEventListener('wheel', this._onWheel);
        el.removeEventListener('pointerdown', this._onDown);
        el.removeEventListener('click', this._onClick, true);
    }

    // 当前停在第几个子项：取 offsetLeft 距 scrollLeft 最近的那个。
    //
    // 不按 `scrollLeft / 单项宽` 算：那要求所有项等宽（carousel 是，分类胶囊
    // 不是），换个调用点就悄悄算错。逐项比距离对两种都成立，代价是 O(n)，而 n
    // 是一屏的卡片数。
    _pageIndex() {
        const el = this._el;
        if (!el) return 0;
        const kids = el.children;
        let best = 0;
        let bestD = Infinity;
        for (let i = 0; i < kids.length; i++) {
            const d = Math.abs(kids[i].offsetLeft - el.scrollLeft);
            if (d < bestD) { bestD = d; best = i; }
        }
        return best;
    }

    // rAF 节流：滚动期间每帧最多量一次
    _sched = () => {
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => {
            this._raf = 0;
            const el = this._el;
            if (!el) return;
            const max = el.scrollWidth - el.clientWidth;
            const atStart = max <= 1 || el.scrollLeft <= 1;
            const atEnd = max <= 1 || el.scrollLeft >= max - 1;
            if (this.state.atStart !== atStart || this.state.atEnd !== atEnd) {
                this.setState({ atStart, atEnd });
            }
            // onPageChange：翻页指示点（R10）的驱动。**只在真的换页时**回调 ——
            // 每帧都喊会把父组件的 setState 拉进滚动的每一帧，而 carousel 的父
            // 组件是整条消息列表。
            if (this.props.onPageChange) {
                const i = this._pageIndex();
                if (i !== this._page) {
                    this._page = i;
                    this.props.onPageChange(i);
                }
            }
        });
    }

    // 纵向滚轮 → 横向滚动。只有真溢出、且这一下主要是纵向时才拦；到头了也放行，
    // 否则外层面板的纵向滚动会被这一行锁死。
    _onWheel = (e) => {
        const el = this._el;
        const max = el.scrollWidth - el.clientWidth;
        if (max <= 1 || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
        if ((e.deltaY < 0 && el.scrollLeft <= 0) || (e.deltaY > 0 && el.scrollLeft >= max)) return;
        e.preventDefault();
        el.scrollLeft += e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
        this._sched();
    }

    _onDown = (e) => {
        const el = this._el;
        // 触摸/笔一律走原生滑动，JS 插手只会打架
        if (e.pointerType === 'touch' || e.button !== 0) return;
        if (el.scrollWidth - el.clientWidth <= 1) return;
        this._drag = { id: e.pointerId, x: e.clientX, left: el.scrollLeft, moved: false };
        el.addEventListener('pointermove', this._onMove);
        el.addEventListener('pointerup', this._onUp);
        el.addEventListener('pointercancel', this._onUp);
    }

    _onMove = (e) => {
        const el = this._el;
        const d = this._drag;
        if (!d || e.pointerId !== d.id) return;
        const dx = e.clientX - d.x;
        if (!d.moved) {
            if (Math.abs(dx) < DRAG_THRESHOLD) return;   // 阈值内仍算“点”
            d.moved = true;
            try { el.setPointerCapture(d.id); } catch (_) { /* 捕获失败不致命 */ }
            el.style.userSelect = 'none';
            el.style.scrollBehavior = 'auto';            // 拖拽跟手，不许补间
            this._cursor('grabbing');
        }
        e.preventDefault();
        el.scrollLeft = d.left - dx;
        this._sched();
    }

    _onUp = () => {
        const el = this._el;
        const d = this._drag;
        this._drag = null;
        el.removeEventListener('pointermove', this._onMove);
        el.removeEventListener('pointerup', this._onUp);
        el.removeEventListener('pointercancel', this._onUp);
        if (!d) return;
        try { el.releasePointerCapture(d.id); } catch (_) { /* 没捕获过 */ }
        el.style.userSelect = '';
        el.style.scrollBehavior = '';
        this._cursor('');
        if (d.moved) {
            // 紧随其后的那次 click 是拖拽的尾巴，吞掉；没有 click 的话由
            // 宏任务兜底清标志，否则会误伤下一次真点击。
            this._eat = true;
            setTimeout(() => { this._eat = false; }, 0);
        }
    }

    _onClick = (e) => {
        if (!this._eat) return;
        this._eat = false;
        e.preventDefault();
        e.stopPropagation();
    }

    // 子项自带 cursor:pointer，会盖掉容器的 grabbing；结束时还原成它们原来的
    // 内联值（直接清空会把 React 写的 cursor:pointer 抹掉且不会补回来）。
    _cursor(v) {
        const kids = this._el.children;
        for (let i = 0; i < kids.length; i++) {
            const c = kids[i];
            if (!c.style) continue;
            if (v) { c._lcCur = c.style.cursor; c.style.cursor = v; }
            else { c.style.cursor = c._lcCur || ''; }
        }
        this._el.style.cursor = v;
    }

    // 箭头：滚一屏的 80%（一屏满滚会丢掉边界上下文）
    _nudge = (dir) => {
        const el = this._el;
        if (!el) return;
        el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    render() {
        const { children, className, style, wrapperStyle, fade = true, arrows = true,
                accent = 'var(--lc-main)', fadeFrom = 'var(--lc-bg, #f8fafc)',
                fadeTo = 'transparent' } = this.props;
        const { atStart, atEnd, hover } = this.state;

        const transition = prefersReducedMotion() ? 'none' : 'opacity var(--lc-dur-fast) var(--lc-ease-out)';
        const mask = (side, dir, hidden) => (
            <div aria-hidden="true" style={{
                ...MASK,
                [side]: 0,
                opacity: hidden ? 0 : 1,
                transition,
                background: `linear-gradient(to ${dir}, ${fadeFrom}, ${fadeTo})`,
            }} />
        );

        // 箭头浮在遮罩之上，只在鼠标悬停这一行时淡入；该侧到头就没有
        const arrow = (side, hidden) => (
            <button
                type="button"
                tabIndex={-1}
                aria-hidden="true"
                onClick={() => this._nudge(side === 'left' ? -1 : 1)}
                style={{
                    position: 'absolute',
                    [side]: '2px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '28px',
                    height: '28px',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 'var(--lc-radius-full)',
                    border: '1px solid #e2e8f0',
                    background: '#fff',
                    boxShadow: 'var(--lc-shadow-2)',
                    color: accent,
                    cursor: 'pointer',
                    opacity: hidden || !hover ? 0 : 1,
                    pointerEvents: hidden || !hover ? 'none' : 'auto',
                    transition,
                }}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                    <polyline points={side === 'left' ? '15 18 9 12 15 6' : '9 18 15 12 9 6'} />
                </svg>
            </button>
        );

        return (
            <div
                style={{ position: 'relative', ...wrapperStyle }}
                onPointerEnter={this._fine ? () => this.setState({ hover: true }) : undefined}
                onPointerLeave={this._fine ? () => this.setState({ hover: false }) : undefined}
            >
                <div ref={this.setRef} className={className} style={{ ...SCROLLER, ...style }}>
                    {children}
                </div>
                {fade && mask('left', 'right', atStart)}
                {fade && mask('right', 'left', atEnd)}
                {arrows && this._fine && arrow('left', atStart)}
                {arrows && this._fine && arrow('right', atEnd)}
            </div>
        );
    }
}
